import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { DateField } from '../../../components/inputs/DateField';
import { Select } from '../../../components/inputs/Select';
import { TextField } from '../../../components/inputs/TextField';
import { Textarea } from '../../../components/inputs/Textarea';
import { todayCalendarDate } from '../../../components/calendar/monthGrid';
import { ApiError } from '../../../lib/apiError';
import { formatDateOnly } from '../../../lib/formatDate';
import { focusValidationError } from '../../../lib/validationFocus';
import { uid } from '../../../lib/uid';
import { useStaffOptions } from '../../human-resources/personnel/useStaff';
import { disbursementRequestsApi } from '../disbursementRequestsApi';
import type { DisbursementRequestDetail } from '../disbursementRequestTypes';

/*
  The three steps that COLLECT a value, each a form with its own tick — a step that collects is
  a form rather than a confirmation dialog, because a dialog cannot ask for a value and one
  stacked over a form would break "one modal, never stacked". The step that collects nothing —
  passing a request to Accounting — is a `<ConfirmDialog>` on the profile instead.

  Each carries Autogenerate, like every modal that writes: the numbers it fills are obviously
  sample values (`DV-SAMPLE-…`, `SAMPLE-…`), so a generated release can never be mistaken for a
  real voucher or cheque.
*/

type Request = Pick<DisbursementRequestDetail, 'id' | 'requestNumber' | 'voucherDate' | 'requesterPersonnelId'>;

function ErrorStrip({ error }: { error: ApiError | null }) {
  return error ? (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
      <p className="text-xs text-red-700">{error.message}</p>
    </div>
  ) : null;
}

/** The voucher came back from Accounting: Cash records its number and date. */
export function ReceiveVoucherModal({
  request,
  onClose,
  onSaved,
}: {
  request: Request;
  onClose: () => void;
  onSaved: (saved: DisbursementRequestDetail) => void;
}) {
  const [voucherNumber, setVoucherNumber] = useState('');
  const [voucherDate, setVoucherDate] = useState(todayCalendarDate());
  // Seeded with the requesting party so the field is never empty; the clerk's own record replaces it below.
  const [receivedBy, setReceivedBy] = useState<string | null>(request.requesterPersonnelId);
  const receivedByTouched = useRef(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  /*
    RECEIVED BY IS AUTOFILLED and stays the reader's to change — product call, 09 Oct 2026.
    First choice: the signed-in clerk's own employee record, the person at the keyboard. An
    account with no employee record (the bootstrap admin) keeps the FALLBACK it opened with —
    the request's requesting party — so the field is never left empty. The lookup lands after
    the form opens, so it cannot be initial state, and it never overwrites a choice the reader
    has already made.
  */
  useEffect(() => {
    const controller = new AbortController();

    disbursementRequestsApi
      .me(controller.signal)
      .then((me) => {
        if (controller.signal.aborted || me.personnelId == null || receivedByTouched.current) return;
        setReceivedBy(me.personnelId);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  /*
    A default the picker cannot SHOW is not a default. The fallback is the requester, whose
    record may since have been deactivated — then it is not among the active staff offered, the
    field would hold an id it cannot display, and the server would refuse it. Derived during
    render, so the field reads empty and the reader picks somebody.
  */
  const receivedByShown =
    receivedBy != null && (loadingStaff || staff.some((o) => o.value === receivedBy)) ? receivedBy : null;

  const today = todayCalendarDate();
  const valid = voucherNumber.trim() !== '' && voucherDate !== '' && voucherDate <= today && receivedByShown != null;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await disbursementRequestsApi.receiveVoucher(request.id, {
          voucherNumber: voucherNumber.trim(),
          voucherDate,
          receivedByPersonnelId: receivedByShown,
        }),
      );
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the voucher.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'receive-voucher' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Receive voucher for ${request.requestNumber}`}
      size="lg"
      id="receive-voucher-modal"
      autogenerate={{
        onGenerate: () => {
          setVoucherNumber(`DV-SAMPLE-${uid().slice(0, 6).toUpperCase()}`);
          setVoucherDate(todayCalendarDate());
          // A person the reader already chose — or the default — is left alone.
          if (receivedByShown == null && staff.length > 0) {
            setReceivedBy(staff[Math.floor(Math.random() * staff.length)].value);
          }
        },
        disabled: saving || loadingStaff,
      }}
      confirm={{ id: 'receive-voucher-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="receive-voucher-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="receive-voucher-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorStrip error={error} />

        <p className="text-xs text-slate-600">
          Accounting has processed this request and issued its disbursement voucher. Recording it
          puts the request <strong>for release</strong>, and the voucher number cannot be changed
          afterwards.
        </p>

        <div className="flex flex-col gap-4">
          <TextField
            id="receive-voucher-voucher-number"
            label="Voucher number"
            required
            className="font-mono"
            description="The disbursement voucher number, exactly as Accounting printed it."
            value={voucherNumber}
            onChange={(e) => setVoucherNumber(e.target.value)}
            error={voucherNumber.trim() ? error?.fieldError('voucherNumber') : 'Enter the disbursement voucher number.'}
            disabled={saving}
          />

          <DateField
            id="receive-voucher-voucher-date"
            label="Voucher date"
            required
            max={today}
            description="The date on the voucher."
            value={voucherDate}
            onChange={setVoucherDate}
            error={
              voucherDate === ''
                ? 'Enter the date on the voucher.'
                : voucherDate > today
                  ? 'The voucher date cannot be in the future.'
                  : error?.fieldError('voucherDate')
            }
            disabled={saving}
          />

          <Select
            id="receive-voucher-received-by-personnel-id"
            label="Received by"
            required
            description="Who took the voucher back from Accounting. Filled with your own employee record — or the requesting party, where your account has none. Change it if somebody else received it."
            value={receivedByShown}
            onChange={(next) => {
              receivedByTouched.current = true;
              setReceivedBy(next || null);
            }}
            options={staff}
            loading={loadingStaff}
            placeholder="Choose an employee"
            remote
            error={receivedByShown != null ? error?.fieldError('receivedByPersonnelId') : 'Choose who received the voucher.'}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}

/** Cash pays it out to the requesting party. */
export function ReleaseDisbursementModal({
  request,
  onClose,
  onSaved,
}: {
  request: Request;
  onClose: () => void;
  onSaved: (saved: DisbursementRequestDetail) => void;
}) {
  const today = todayCalendarDate();
  const [disbursedOn, setDisbursedOn] = useState(today);
  const [reference, setReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // `!= null` — a voucher date omitted from the wire arrives undefined.
  const floor = request.voucherDate != null ? request.voucherDate : undefined;
  const dateError =
    disbursedOn === ''
      ? 'Enter the date it was released.'
      : disbursedOn > today
        ? 'The release date cannot be in the future.'
        : floor != null && disbursedOn < floor
          ? `The release date cannot be before the voucher date, ${formatDateOnly(floor)}.`
          : undefined;

  const valid = dateError === undefined && reference.trim() !== '';
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(await disbursementRequestsApi.release(request.id, { disbursedOn, reference: reference.trim() }));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the release.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'release-disbursement' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Release ${request.requestNumber}`}
      size="lg"
      id="release-disbursement-modal"
      autogenerate={{
        onGenerate: () => {
          setReference(`SAMPLE-${uid().slice(0, 6).toUpperCase()}`);
          setDisbursedOn(todayCalendarDate());
        },
        disabled: saving,
      }}
      confirm={{ id: 'release-disbursement-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="release-disbursement-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="release-disbursement-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorStrip error={error} />

        <p className="text-xs text-slate-600">
          This records that the voucher was paid out to the requesting party. The request is then{' '}
          <strong>disbursed</strong>, which is final — it cannot be returned or deleted afterwards.
        </p>

        <div className="flex flex-col gap-4">
          <DateField
            id="release-disbursement-disbursed-on"
            label="Released on"
            required
            min={floor}
            max={today}
            description="The day the payment went out."
            value={disbursedOn}
            onChange={setDisbursedOn}
            error={dateError ?? error?.fieldError('disbursedOn')}
            disabled={saving}
          />

          <TextField
            id="release-disbursement-reference"
            label="Payment reference"
            required
            className="font-mono"
            description="The cheque number, LD.ADA reference or EMDS reference the payment went out under."
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            error={reference.trim() ? error?.fieldError('reference') : 'Enter the cheque number or payment reference.'}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}

/** Sends a request back, with a reason, while it is with Cash or at Accounting. */
export function ReturnDisbursementModal({
  request,
  atAccounting,
  onClose,
  onSaved,
}: {
  request: Request;
  atAccounting: boolean;
  onClose: () => void;
  onSaved: (saved: DisbursementRequestDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = reason.trim() !== '' && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(await disbursementRequestsApi.returnRequest(request.id, reason.trim()));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not return this request.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'return-disbursement' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Return ${request.requestNumber}`}
      size="lg"
      id="return-disbursement-modal"
      autogenerate={{
        onGenerate: () => setReason('Sample return — supporting documents are incomplete.'),
        disabled: saving,
      }}
      confirm={{ id: 'return-disbursement-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="return-disbursement-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="return-disbursement-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorStrip error={error} />

        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <p>
            <strong>Returned is final.</strong> The request stays on the register, marked returned,
            with this reason against it. A corrected request is raised as a new one.
          </p>
          {atAccounting ? (
            <p>Use this when Accounting has sent the request back without a voucher.</p>
          ) : null}
          <p>
            If it was keyed against the wrong employee, or twice, <strong>Delete</strong> is the one
            you want instead.
          </p>
        </div>

        <Textarea
          id="return-disbursement-reason"
          label="Reason"
          required
          description="Why the request cannot go forward. It stays on the record."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being returned.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
